import { useEffect, useRef, useState } from "react"
import L from "leaflet"
import "leaflet.gridlayer.googlemutant"
import { Marker } from "@adamscybot/react-leaflet-component-marker"
import {
  MapContainer,
  MapContainerProps,
  TileLayer,
  useMap,
  useMapEvents,
} from "react-leaflet"
import { Icons } from "@/components/icons"
import { usePosition } from "@/components/providers/position-provider"
import { GoogleMapsLayer, RadioCardsLayer } from '../map-google'
import { Button } from '@/components/ui/button'

// Define the MapProps interface
interface MapProps {
  position: [number, number];
}

function ClickHandler({ onClick }: { onClick: (latlng: [number, number]) => void }) {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng
      onClick([lat, lng])
    }
  })
  return null
}

// Draggable Marker
function DraggableMarker({
  initialPosition,
  setStreetViewCoords,
}: {
  initialPosition: [number, number]
  setStreetViewCoords: (coords: [number, number]) => void
}) {
  const { onPositionChange } = usePosition()
  const [position, setPosition] = useState<[number, number]>(initialPosition)

  useMapEvents({
    dragend: (event) => {
      const marker = event.target
      const { lat, lng } = marker.getLatLng()
      setPosition([lat, lng])
      onPositionChange([lat, lng])
      setStreetViewCoords([lat, lng])
    },
  })

  return (
    <Marker
      icon={<Icons.user className="fill-primary" />}
      position={position}
      draggable={true}
      eventHandlers={{
        dragend: (e) => {
          const marker = e.target
          const { lat, lng } = marker.getLatLng()
          onPositionChange([lat, lng])
          setPosition([lat, lng])
          setStreetViewCoords([lat, lng])
        },
      }}
    />
  )
}

function ReFocusOnConsumer({ markerPosition }: { markerPosition: [number, number] }) {
  const map = useMap()

  const handleRefocus = () => {
    map.setView(markerPosition, map.getZoom(), { animate: true })
  }

  return <Button type="button" variant="link" onClick={handleRefocus}>📍 Locate</Button>
}
// OpenStreetMap Fallback Layer
const OpenStreetMapLayer = () => (
  <TileLayer
    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    maxZoom={18}
  />
)

// Main Map Component
export function MapPicker({ position }: MapProps) {
  const [googleFailed, setGoogleFailed] = useState(false)
  const [mapType, setMapType] = useState<"roadmap" | "hybrid">("roadmap")
  const [streetViewCoords, setStreetViewCoords] = useState<[number, number] | null>(null)
  const [markerPosition, setMarkerPosition] = useState<[number, number]>(position)

  const streetViewRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (streetViewCoords && window.google && streetViewRef.current) {
      const panorama = new window.google.maps.StreetViewPanorama(streetViewRef.current, {
        position: { lat: streetViewCoords[0], lng: streetViewCoords[1] },
        pov: { heading: 165, pitch: 0 },
        zoom: 1,
      })
    }
  }, [streetViewCoords])

  const mapOptions: MapContainerProps = {
    center: position,
    zoom: 16,
    minZoom: 13,
    maxZoom: googleFailed ? 18 : 22, // with Google fallback
    className: "h-screen w-full",
    scrollWheelZoom: true,
    doubleClickZoom: false,
    attributionControl: false,
  }

  return (
    <div className="flex flex-col gap-1 pb-1">
      <MapContainer {...mapOptions}>
        {googleFailed ? (
          <OpenStreetMapLayer />
        ) : (
          <GoogleMapsLayer mapType={mapType} onError={() => setGoogleFailed(true)} />
        )}
        <DraggableMarker
          initialPosition={position}
          setStreetViewCoords={(coords) => {
            setStreetViewCoords(coords)
            setMarkerPosition(coords)
          }}
        />

        {/* ✅ Street View at top-right corner */}
        {streetViewCoords && (
          <div className="absolute bottom-2 right-2 w-80 h-80 z-[1000] rounded-lg shadow-lg bg-white overflow-hidden">
            <div ref={streetViewRef} className="w-full h-full" />
          </div>
        )}

        {/* ✅ Refocus button */}
        <div className="absolute top-2 right-2 z-[1000]">
          <ReFocusOnConsumer markerPosition={markerPosition} />
        </div>
      </MapContainer>

      <RadioCardsLayer setMapType={setMapType} />
    </div>
  )
}